import { Icons } from '@/utils/Icons';
import { Link, useLocation } from 'react-router-dom';
import UserDropdown from './UserDropdown';
import Navbar from '@/components/Navbar';
import HeaderTopGames from './HeaderTopGames';
import MobileNavbar from '@/components/MobileNavbar';
import { useState } from 'react';
import LoginModal from '@/components/modals/LoginModal';
import SetButtonValueModal from './SetButtonValueModal';
import RulesModal from '@/components/RulesModal';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  setAgeModalOpen,
  setChangePasswordModalOpen,
  setLoginModalOpen,
  toggleSidebarCollapsed,
} from '@/store/slices/uiSlice';
import AgeRestrictionModal from '@/components/AgeRestrictionModal';
import {
  useGetContactSupportQuery,
  useGetUserInfoQuery,
  useGetUserWalletsQuery,
} from '@/store/api/userApi';
import { useIsDemoUser } from '@/hooks/redux';
import { formatNumber, isGuestUser } from '@/utils';
import ExposureSummaryModal from './ExposureSummaryModal';

const Header = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const [showBalance, setShowBalance] = useState(true);
  const [showExposure, setShowExposure] = useState(true);
  const [isExposureModalOpen, setIsExposureModalOpen] = useState(false);
  const [isBalanceTooltipOpen, setIsBalanceTooltipOpen] = useState(false);
  const loginOpen = useAppSelector((state) => (state as any).ui.loginModalOpen);
  const location = useLocation();
  const isAuthenticated = !isGuestUser();
  const reportsPage = location.pathname.includes('user');
  const isDashboard = location.pathname.includes('dashboard');
  const dispatch = useAppDispatch();
  const dashboardPage = location.pathname.includes('dashboard');
  const {
    ageModalOpen,
    changePasswordModalOpen,
    sidebarCollapsed: isSidebarCollapsed,
  } = useAppSelector((state) => state.ui);
  useGetUserInfoQuery(undefined, { pollingInterval: 5000, skip: !isAuthenticated });
  useGetUserWalletsQuery(undefined, { pollingInterval: 5000, skip: !isAuthenticated });
  useGetContactSupportQuery(undefined);
  const { info, walletInfo } = useAppSelector((state) => state.user);
  const username = info?.username ?? 'Demo';
  const isDemoUser = useIsDemoUser();
  const mainWallet = walletInfo?.find((item) => item?.type?.toLowerCase() == 'main');
  const mainBalance = Number(
    Number(mainWallet?.amount ?? 0) -
      Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
      Number(mainWallet?.lockedAmount ?? 0)
  ).toFixed(2);

  const exposure = Number(mainWallet?.exposureAmount ?? 0).toFixed(2);

  const handleBalanceChange = (value: boolean) => {
    setShowBalance(value);
  };

  const handleExposureChange = (value: boolean) => {
    setShowExposure(value);
  };

  return (
    <div className="bg-primary">
      {/* Header Top */}
      <section className="max-xl:px-2 xl:px-20 xl:py-2">
        <div className="flex justify-between relative items-center z-50">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="pb-1">
              <img
                className={`h-auto max-h-[65px] w-auto object-contain transition-all duration-300 ${
                  isSidebarCollapsed && !dashboardPage ? 'xl:max-h-[40px]' : ''
                }`}
                src="/images/logo/logo.png"
                alt=""
              />
            </Link>
            {!dashboardPage && (
              <button
                type="button"
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => dispatch(toggleSidebarCollapsed())}
                className="hidden xl:flex size-10 items-center justify-center text-white transition hover:bg-white/10 ml-6"
              >
                <Icons.Menu className="text-2xl" />
              </button>
            )}
            {/* <button
              type="button"
              aria-label="Open menu"
              onClick={() => onMenuClick?.()}
              className="hidden xl:flex size-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition hover:bg-white/10"
            >
              <Icons.Menu className="text-lg" />
            </button> */}
          </div>
          {/* Details */}
          <div className="flex items-center text-white space-x-1">
            {/* <SearchDropdown variant="compact" />
            <div style={{ lineHeight: 'normal' }} className="text-base font-bold">
              <div className="cursor-pointer" onClick={() => dispatch(setRulesModalOpen(true))}>
                Rules
              </div>
            </div> */}
            {!isAuthenticated ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => dispatch(setLoginModalOpen(true))}
                  className="rounded-full border-2 border-white bg-white px-3 py-2 text-sm font-semibold uppercase hover:text-white shadow-[inset_0_0_0_2px_#16a6b5] transition hover:bg-button-primary"
                >
                  Sign in
                </button>
              </div>
            ) : (
              <>
                {/* Balance Info Toggle */}
                <div className="relative">
                  <button
                    onClick={() => setIsBalanceTooltipOpen(!isBalanceTooltipOpen)}
                    className="bg-secondary-dark border-2 border-gray-600 px-0.5 py-1  flex items-center text-white text-xs md:text-base  gap-1 hover:bg-gray-700"
                  >
                    <img src="/images/coin.svg" alt="wallet" className="size-4 md:size-5" />
                    <span className="text-white text-sm font-semibold md:text-base ">
                      {formatNumber(mainBalance)}
                    </span>
                    <span
                      className={`transition-transform ${isBalanceTooltipOpen ? 'rotate-180' : ''}`}
                    >
                      <Icons.Down />
                    </span>
                  </button>

                  {/* Balance Tooltip */}
                  {isBalanceTooltipOpen && (
                    <div className="absolute top-full left-0 mt-2 bg-white border-2 border-gray-300 rounded shadow-lg p-4 min-w-[200px] z-10">
                      <div className="mb-3">
                        <div className="text-gray-600 text-sm">Balance:</div>
                        <div className="text-gray-800 font-bold text-lg">{mainBalance}</div>
                      </div>
                      {showExposure && (
                        <>
                          <div className="border-t border-gray-300 pt-3">
                            <div className="text-gray-600 text-sm">Exposure:</div>
                            <div className="text-red-600 font-bold text-lg">{exposure}</div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <UserDropdown
                    isDemoUser={isDemoUser}
                    showBalance={showBalance}
                    showExposure={showExposure}
                    handleBalanceChange={handleBalanceChange}
                    handleExposureChange={handleExposureChange}
                    username={username}
                  />
                </div>
              </>
            )}
            {!dashboardPage && (
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => onMenuClick?.()}
                className="xl:hidden flex items-center justify-center bg-white/5 text-white transition hover:bg-white/10"
              >
                <Icons.Menu className="text-lg" />
              </button>
            )}
          </div>

          {/* <Marquee /> */}
        </div>
        {/* <div className="xl:hidden flex items-center gap-1">
          <SearchDropdown variant="compact" />
          <div className="whitespace-nowrap overflow-hidden py-[3px] flex-1 text-body-inverse text-sm italic bg-danger-coral">
            Newly Launched Matka Market in Our Exchange
          </div>
        
        </div> */}
      </section>

      {/* Header Bottom */}
      <section>
        {!reportsPage && (
          <>
            <HeaderTopGames />
          </>
        )}
        <Navbar />{' '}
        {!isDashboard && <MobileNavbar />}
        <SetButtonValueModal />
        <RulesModal />
        <AgeRestrictionModal open={ageModalOpen} onClose={() => dispatch(setAgeModalOpen(false))} />
        <ChangePasswordModal
          open={changePasswordModalOpen}
          onClose={() => dispatch(setChangePasswordModalOpen(false))}
        />
        <ExposureSummaryModal
          open={isExposureModalOpen}
          onClose={() => setIsExposureModalOpen(false)}
        />
        <LoginModal open={loginOpen} onClose={() => dispatch(setLoginModalOpen(false))} />
      </section>
    </div>
  );
};

export default Header;
